import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "tycopro_cookie_consent";
const CONSENT_VERSION = 1;
const OPEN_EVENT = "tycopro:open-cookie-settings";

/**
 * Réouvre le panneau de gestion des cookies depuis n'importe où
 * (lien « Gérer les cookies » du footer et de la page /cookies).
 */
export function openCookieSettings() {
    window.dispatchEvent(new Event(OPEN_EVENT));
}

// Lit le consentement stocké en gérant l'ancien format booléen ("true"/"false")
// et le nouveau format structuré (JSON par catégorie). Renvoie null si aucun
// choix n'a encore été exprimé.
function readConsent() {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        if (raw === "true") return { categories: { necessary: true, analytics: true } };
        if (raw === "false") return { categories: { necessary: true, analytics: false } };
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

/**
 * Bannière de consentement cookies (RGPD/CNIL).
 * - « Tout accepter » et « Refuser » au même niveau (pas de choix pré-coché).
 * - « Personnaliser » ouvre un panneau granulaire (catégorie Nécessaires
 *   toujours active, Mesure d'audience désactivée par défaut).
 * - Le choix est mémorisé jusqu'à 13 mois et peut être modifié à tout moment
 *   via « Gérer les cookies ».
 */
export default function CookieBanner() {
    const [visible, setVisible] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [analytics, setAnalytics] = useState(false);

    useEffect(() => {
        if (readConsent() === null) setVisible(true);
        const open = () => {
            const c = readConsent();
            setAnalytics(!!(c && c.categories && c.categories.analytics));
            setShowDetails(true);
            setVisible(true);
        };
        window.addEventListener(OPEN_EVENT, open);
        return () => window.removeEventListener(OPEN_EVENT, open);
    }, []);

    const persist = (analyticsAllowed) => {
        const consent = {
            version: CONSENT_VERSION,
            date: new Date().toISOString(),
            categories: { necessary: true, analytics: analyticsAllowed },
        };
        try {
            localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
        } catch (e) {}
        setVisible(false);
        setShowDetails(false);
        if (analyticsAllowed) {
            if (typeof window.__tycoproInitAnalytics === "function") {
                window.__tycoproInitAnalytics();
            }
            if (window.posthog && typeof window.posthog.opt_in_capturing === "function") {
                window.posthog.opt_in_capturing();
            }
        } else if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
            // Retrait du consentement : on coupe toute capture déjà active.
            window.posthog.opt_out_capturing();
        }
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-label="Consentement aux cookies"
            data-testid="cookie-banner"
            className="fixed bottom-0 inset-x-0 z-[60] bg-primary-950 text-primary-100 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.4)]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-6">
                    <p className="text-sm leading-relaxed flex-1">
                        Nous utilisons des cookies de mesure d'audience pour améliorer le site. Aucune
                        donnée n'est utilisée à des fins publicitaires.{" "}
                        <Link to="/cookies" className="underline underline-offset-2 hover:text-white">
                            En savoir plus
                        </Link>
                    </p>
                    <div className="flex flex-wrap gap-2 shrink-0">
                        <button
                            onClick={() => setShowDetails((v) => !v)}
                            data-testid="cookie-customize"
                            className="px-4 py-2 rounded-xl border border-white/25 text-sm hover:bg-white/10 transition"
                        >
                            Personnaliser
                        </button>
                        <button
                            onClick={() => persist(false)}
                            data-testid="cookie-refuse"
                            className="px-4 py-2 rounded-xl border border-white/25 text-sm hover:bg-white/10 transition"
                        >
                            Refuser
                        </button>
                        <button
                            onClick={() => persist(true)}
                            data-testid="cookie-accept"
                            className="px-5 py-2 rounded-xl bg-white text-primary-800 text-sm font-semibold hover:bg-primary-50 transition"
                        >
                            Tout accepter
                        </button>
                    </div>
                </div>

                {showDetails && (
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                        <div className="flex items-start justify-between gap-4 text-sm">
                            <div>
                                <p className="font-semibold text-white">Cookies strictement nécessaires</p>
                                <p className="text-primary-300">
                                    Indispensables au fonctionnement du site (mémorisation de vos choix).
                                    Toujours actifs.
                                </p>
                            </div>
                            <span className="text-primary-300 text-xs whitespace-nowrap pt-1">
                                Toujours actif
                            </span>
                        </div>
                        <div className="flex items-start justify-between gap-4 text-sm">
                            <div>
                                <p className="font-semibold text-white">Mesure d'audience</p>
                                <p className="text-primary-300">
                                    Statistiques de visite anonymisées (PostHog, hébergé dans l'UE) pour
                                    améliorer le site.
                                </p>
                            </div>
                            <label className="inline-flex items-center cursor-pointer pt-1">
                                <input
                                    type="checkbox"
                                    data-testid="cookie-toggle-analytics"
                                    checked={analytics}
                                    onChange={(e) => setAnalytics(e.target.checked)}
                                    className="h-4 w-4 accent-white"
                                />
                            </label>
                        </div>
                        <div className="flex justify-end">
                            <button
                                onClick={() => persist(analytics)}
                                data-testid="cookie-save"
                                className="px-5 py-2 rounded-xl bg-white text-primary-800 text-sm font-semibold hover:bg-primary-50 transition"
                            >
                                Enregistrer mes choix
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
